import { ReviewSearchParamsDto } from '@/contracts/marketplace/reviews';
import { ReviewsPageAction } from '@/context/reviews-page.state-machine';
import {
    AiSelectedAnalysisProgress,
    ReviewsAutoReplyProgress,
    ReviewsExportProgress,
} from '@/context/reviews-page.types';
import { BrandGroupEntity } from '@/entities/brand-group.entity';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import { ReviewEntity } from '@/entities/review.entity';
import { Sort } from '@/types/sort.type';
import { SetStateAction, useCallback } from 'react';

type ReviewsPageDispatch = (action: ReviewsPageAction) => void;

export const useReviewsPageActions = (dispatch: ReviewsPageDispatch) => {
    const setSelectionSessionId = useCallback(
        (value: string) => {
            dispatch({ type: 'SET_SELECTION_SESSION_ID', value });
        },
        [dispatch],
    );
    const setPickedReviews = useCallback(
        (value: string[]) => {
            dispatch({ type: 'SET_PICKED_REVIEWS', value });
        },
        [dispatch],
    );
    const setPickedProducts = useCallback(
        (value: string[]) => {
            dispatch({ type: 'SET_PICKED_PRODUCTS', value });
        },
        [dispatch],
    );
    const setPickedBrandGroups = useCallback(
        (value: string[]) => {
            dispatch({ type: 'SET_PICKED_BRAND_GROUPS', value });
        },
        [dispatch],
    );
    const setSort = useCallback(
        (value: Sort) => {
            dispatch({ type: 'SET_SORT', value });
        },
        [dispatch],
    );
    const setFilters = useCallback(
        (value: SetStateAction<ReviewSearchParamsDto>) => {
            dispatch({ type: 'SET_FILTERS', value });
        },
        [dispatch],
    );
    const setAiAnalysisProgress = useCallback(
        (value: SetStateAction<AiSelectedAnalysisProgress | null>) => {
            dispatch({ type: 'SET_AI_ANALYSIS_PROGRESS', value });
        },
        [dispatch],
    );
    const setExportProgress = useCallback(
        (value: SetStateAction<ReviewsExportProgress | null>) => {
            dispatch({ type: 'SET_EXPORT_PROGRESS', value });
        },
        [dispatch],
    );
    const setAutoReplyProgress = useCallback(
        (value: SetStateAction<ReviewsAutoReplyProgress | null>) => {
            dispatch({ type: 'SET_AUTO_REPLY_PROGRESS', value });
        },
        [dispatch],
    );
    const setProductsState = useCallback(
        (value: ProductGroupEntity[]) => {
            dispatch({ type: 'SET_PRODUCTS', value });
        },
        [dispatch],
    );
    const setBrandGroupsState = useCallback(
        (value: BrandGroupEntity[]) => {
            dispatch({ type: 'SET_BRAND_GROUPS', value });
        },
        [dispatch],
    );
    const setReviewsAndTotalState = useCallback(
        (nextReviews: ReviewEntity[], nextTotal: number) => {
            dispatch({
                type: 'SET_REVIEWS_AND_TOTAL',
                reviews: nextReviews,
                total: nextTotal,
            });
        },
        [dispatch],
    );
    const setReviewsOnPageState = useCallback(
        (value: number) => {
            dispatch({ type: 'SET_REVIEWS_ON_PAGE', value });
        },
        [dispatch],
    );
    const applyReviewAnswerJobDone = useCallback(
        (review: ReviewEntity) => {
            dispatch({ type: 'APPLY_REVIEW_ANSWER_JOB_DONE', review });
        },
        [dispatch],
    );
    const applyReviewFeedItemChanged = useCallback(
        (review: ReviewEntity) => {
            dispatch({ type: 'APPLY_REVIEW_FEED_ITEM_CHANGED', review });
        },
        [dispatch],
    );

    return {
        setSelectionSessionId,
        setPickedReviews,
        setPickedProducts,
        setPickedBrandGroups,
        setSort,
        setFilters,
        setAiAnalysisProgress,
        setExportProgress,
        setAutoReplyProgress,
        setProductsState,
        setBrandGroupsState,
        setReviewsAndTotalState,
        setReviewsOnPageState,
        applyReviewAnswerJobDone,
        applyReviewFeedItemChanged,
    };
};
